import {
  Box,
  Image,
  ProgressBar,
  Styles,
  Text,
  useStyle,
} from '@bonanza-social/moonlight';
import { memo, useCallback, useEffect, useMemo } from 'react';
import { Pressable, ViewStyle } from 'react-native';
import {
  BottomSheet,
  StackNavigationProp,
  useNavigation,
} from '@bonanza-social/navigation';
import {
  useSharedValue,
  useAnimatedStyle,
  withTiming,
  withSequence,
  Easing,
} from 'react-native-reanimated';
import { useSelector } from '@bonanza-social/toolkit/state';
import { selectStatus } from '../../../state';
import useProgressPercent from '../../../hooks/useProgressPercent';
import StatusHeaderPercent from '../../StatusHeaderPercent/StatusHeaderPercent';
import { PROGRESS_IMAGE_OFFSET } from './StatusHeaderItem.content';

function StatusHeaderItem(props: Styles<ViewStyle>) {
  const status = useSelector(selectStatus);
  const pendingRewardsCount = status?.pendingRewards.length;

  const navigation =
    useNavigation<StackNavigationProp<AppNavigatorParamsList>>();
  const prevPendingRewardsCount = useSharedValue(pendingRewardsCount);

  const { onBarLayout, onPercentLayout, percentX } = useProgressPercent({
    totalLoyaltyPoints: status?.totalLoyaltyPoints,
    accumulatedLoyaltyPoints: status?.accumulatedLoyaltyPoints,
    imageOffset: PROGRESS_IMAGE_OFFSET,
  });

  const handleClick = useCallback(() => {
    if (status?.playerStatus) {
      BottomSheet.open({
        namespace: 'status',
        type: pendingRewardsCount ? 'statusBoxes' : 'legacyStatusInfo',
        borderGradient: `status${status.playerStatus}Background`,
        borderStyle: 'allsides',
        headerImageName: `status.${status.playerStatus}Badge`,
        onInfo: () => navigation.navigate('StatusInfo'),
        scrollable: true,
      });
    }
  }, [pendingRewardsCount, status, navigation]);

  // Animations
  const gradientOffset = useSharedValue(0);
  const gradientOpacity = useSharedValue(0);
  const giftScale = useSharedValue(1);

  useEffect(() => {
    if (
      prevPendingRewardsCount.value !== undefined &&
      pendingRewardsCount &&
      pendingRewardsCount > prevPendingRewardsCount.value
    ) {
      gradientOffset.value = withSequence(
        withTiming(-2, { duration: 400, easing: Easing.inOut(Easing.ease) }),
        withTiming(-2, { duration: 1200 }),
        withTiming(0, { duration: 400, easing: Easing.inOut(Easing.ease) }),
      );

      gradientOpacity.value = withSequence(
        withTiming(1, { duration: 400, easing: Easing.inOut(Easing.ease) }),
        withTiming(1, { duration: 1200 }),
        withTiming(0, { duration: 400, easing: Easing.inOut(Easing.ease) }),
      );

      giftScale.value = withSequence(
        withTiming(1.5, { duration: 400, easing: Easing.inOut(Easing.ease) }),
        withTiming(1.5, { duration: 1200 }),
        withTiming(1, { duration: 400, easing: Easing.inOut(Easing.ease) }),
      );
    }

    prevPendingRewardsCount.value = pendingRewardsCount;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingRewardsCount]);

  const gradientStyle = useAnimatedStyle(() => ({
    top: gradientOffset.value,
    bottom: gradientOffset.value,
    left: gradientOffset.value,
    right: gradientOffset.value,
    opacity: gradientOpacity.value,
  }));

  const giftboxStyle = useAnimatedStyle(() => ({
    transform: [{ scale: giftScale.value }],
  }));

  const pressableStyle = useStyle<ViewStyle>({
    height: 'space-1000',
    padding: 'space-200',
    gap: 'space-100',
    justifyContent: 'center',
  });

  if (!status) {
    // TODO: Return proper skeleton
    return (
      <Box
        testID="status-header-item-legacy-placeholder"
        height="space-1000"
        variant="default"
        backgroundColor="secondaryBackgroundDefault"
        borderRadius="radius-200"
        position="relative"
        {...props}
      />
    );
  }

  return (
    <Box
      testID="status-header-item-legacy"
      height="space-1000"
      variant="default"
      backgroundColor="secondaryBackgroundDefault"
      borderRadius="radius-200"
      position="relative"
      marginRight="space-200"
      zIndex={1}
      {...props}
    >
      <Box
        variant="gradient"
        position="absolute"
        borderRadius="radius-200"
        zIndex={-1}
        gradient={`status${status.playerStatus}Background`}
        animatedStyle={gradientStyle}
      />
      <Pressable onPress={handleClick} style={pressableStyle}>
        <Box gap="space-100" position="relative">
          <Text
            locNS="status"
            locKey={status.playerStatus}
            variant="nav-label"
            numberOfLines={1}
            gradient={`darkSurfaceStatus${status.playerStatus}`}
          />
          <ProgressBar
            level={pendingRewardsCount}
            from={0}
            to={status.totalLoyaltyPoints}
            progress={status.accumulatedLoyaltyPoints}
            gradientType={`status${status.playerStatus}Background`}
            onLayout={onBarLayout}
            progressGlowColor={`darkSurfaceSolidStatus${status.playerStatus}`}
            renderPercent={(percent) => (
              <StatusHeaderPercent
                percent={percent}
                onPercentLayout={onPercentLayout}
                percentX={percentX}
              />
            )}
          />
        </Box>
        <Box
          position="absolute"
          right="-space-400"
          animatedStyle={giftboxStyle}
        >
          <Image
            name="common.BonanzaBoxLocked"
            cachePolicy="memory"
            width="space-800"
            height="space-800"
          />
          {pendingRewardsCount ? (
            <Box
              position="absolute"
              top="space-0"
              right="space-0"
              width={14}
              height={14}
              variant="gradient"
              gradient="attentionBackground"
              justifyContent="center"
              alignItems="center"
              borderRadius="radius-full"
            >
              <Text variant="badge" color="darkSurfacePrimary">
                {pendingRewardsCount}
              </Text>
            </Box>
          ) : null}
        </Box>
      </Pressable>
    </Box>
  );
}

export default memo(StatusHeaderItem);
